import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Button, Row, Col, Table, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Layout from "../../hocs/Layout";
import OfferWizardWrapper from "../../components/ofertas/OfferWizardWrapper";
import { useOfferWizard } from "../../components/ofertas/OfferWizardContext";
import { get_archivos_oferta, get_archivo_by_id, delete_archivo_oferta } from "../../services/documentos";
import { get_datos_oferta_erp } from "../../services/ofertas";
import { send_document_signaturit } from "../../services/signaturit";
import { getOfferSignaturitEmail } from "../../utils/signaturit";
import SubirArchivoOferta from "../../components/modals/SubirArchivoOferta";
import SendSignaturitModal from "../../components/modals/SendSignaturitModal";

import { FaFileUpload, FaTrash, FaDownload, FaPaperPlane } from 'react-icons/fa';
import { TbReload } from "react-icons/tb";
import { MdDeleteForever, MdDownload } from "react-icons/md";
import { CiWarning } from "react-icons/ci";
import { LuFileUp } from "react-icons/lu";

const OfertaDocumentacionContent = ({ user }) => {
    const navigate = useNavigate();
    const { sector_type, oferta_id } = useParams();
    const { setActionHandler, setIsSubmitting } = useOfferWizard();

    const [loading, setLoading] = useState(false);
    const [archivos, setArchivos] = useState([]);
    const [oferta, setOferta] = useState(null);
    const [showSubirModal, setShowSubirModal] = useState(false);
    const [showSignaturitModal, setShowSignaturitModal] = useState(false);

    const signaturitStatus = [
        { id: "pending", name: "Pendiente de envio", color: "warning" },
        { id: "ready", name: "Pendiente de firma", color: "info" },
        { id: "completed", name: "Firma Finalizada", color: "success" },
        { id: "expired", name: "Plazo expirado", color: "danger" },
        { id: "canceled", name: "Firma cancelada", color: "danger" },
        { id: "error", name: "Error", color: "danger" },
        { id: "declined", name: "Firma declinada", color: "danger" },
        { id: "signing", name: "En proceso de firma", color: "primary" },
        { id: "in_queue", name: "En cola para envio", color: "secondary" },
    ];

    const fetchOferta = useCallback(async () => {
        try {
            const data = await get_datos_oferta_erp(oferta_id);
            setOferta(data);
        } catch (error) {
            console.error("Error loading offer:", error);
            toast.error("Error al cargar datos de la oferta", { autoClose: false });
        }
    }, [oferta_id]);

    const fetchArchivos = useCallback(async () => {
        setLoading(true);
        try {
            const docs = await get_archivos_oferta(oferta_id);
            setArchivos(docs || []);
        } catch (error) {
            console.error("Error loading files:", error);
            toast.error("Error al cargar documentos", { autoClose: false });
        } finally {
            setLoading(false);
        }
    }, [oferta_id]);

    useEffect(() => {
        fetchOferta();
        fetchArchivos();
    }, [fetchOferta, fetchArchivos]);

    const handleFinish = async () => {
        navigate(`/contratos/ofertas/${oferta_id}`);
    };

    useEffect(() => {
        setActionHandler({
            onNext: handleFinish,
            onSave: handleFinish
        });
    }, [setActionHandler]);

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar este archivo?")) {
            try {
                await delete_archivo_oferta(id);
                fetchArchivos();
                toast.success("Archivo eliminado");
            } catch (error) {
                toast.error("Error al eliminar archivo", { autoClose: false });
            }
        }
    };

    const handleDownload = async (attach_id) => {
        try {
            const archivo = await get_archivo_by_id(attach_id);
            Object.keys(archivo).forEach((arch) => {
                const { datas: base64Data, name: originalName } = archivo[arch];
                const { contentType, extension } = determineContentTypeAndExtension(base64Data);

                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: contentType });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = originalName || `archivo.${extension}`;
                link.click();
                URL.revokeObjectURL(link.href);
            });
        } catch (error) {
            console.error("Error downloading file:", error);
            toast.error("Error al descargar archivo",  { autoClose: false });
        }
    };

    const determineContentTypeAndExtension = (base64Data) => {
        if (base64Data.startsWith("iVBORw0KGgo")) return { contentType: "image/png", extension: "png" };
        if (base64Data.startsWith("/9j/")) return { contentType: "image/jpeg", extension: "jpg" };
        if (base64Data.startsWith("JVBERi0")) return { contentType: "application/pdf", extension: "pdf" };
        return { contentType: "application/octet-stream", extension: "bin" };
    };

    const handleSendSignaturit = async (email, type) => {
        if (!oferta) return;
        try {
            await send_document_signaturit(oferta.id, type, email);
            fetchArchivos(); // Refresh status
        } catch (error) {
            console.error("Error sending to Signaturit:", error);
        }
    };

    const getStatusParams = (statusId) => {
        return signaturitStatus.find(s => s.id === statusId) || { name: statusId, color: "secondary" };
    };

    return (
        <div className="py-4">
            <Container>
                <Row className="justify-content-center">
                    <Col lg={12}>
                        <Card className="detail-card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
                            <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 d-flex justify-content-between align-items-center px-4">
                                <h2 className="text-primary fw-bold mb-0">Documentación y Firma</h2>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2"
                                        onClick={() => fetchArchivos()}
                                    >
                                        <TbReload /> Actualizar Estado Firma
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2"
                                        onClick={() => setShowSignaturitModal(true)}
                                    >
                                        <FaPaperPlane /> Enviar a Firmar
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2"
                                        onClick={() => setShowSubirModal(true)}
                                    >
                                        <LuFileUp /> Subir Archivo
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3 text-muted">Cargando documentos...</p>
                                    </div>
                                ) : archivos && archivos.length > 0 ? (
                                    <div className="table-responsive rounded-3 border border-light shadow-sm">
                                        <Table hover borderless className="mb-0 align-middle">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase">Nombre</th>
                                                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase">Tipo</th>
                                                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase">Estado Firma</th>
                                                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase">Fecha</th>
                                                    <th className="py-3 px-4 text-muted small fw-bold text-uppercase text-end">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {archivos.map((file) => {
                                                    const status = getStatusParams(file.signaturit_status);
                                                    return (
                                                        <tr key={file.id}>
                                                            <td className="px-4 fw-bold text-dark">{file.attach_id[1]}</td>
                                                            <td className="px-4 text-muted">{file.tipus_document}</td>
                                                            <td className="px-4">
                                                                {file.signaturit_status && (
                                                                    <span className={`badge bg-light text-${status.color} border border-${status.color} rounded-pill px-3`}>
                                                                        {status.name}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 text-muted">{file.create_date}</td>
                                                            <td className="px-4 text-end">
                                                                <div className="d-flex justify-content-end gap-2">
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Descargar</Tooltip>}>
                                                                        <Button
                                                                            variant="outline-secondary"
                                                                            size="sm"
                                                                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                                                            style={{ width: 32, height: 32 }}
                                                                            onClick={() => handleDownload(file.attach_id[0])}
                                                                        >
                                                                            <MdDownload size={16} />
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Eliminar</Tooltip>}>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                                                            style={{ width: 32, height: 32 }}
                                                                            onClick={() => handleDelete(file.id)}
                                                                        >
                                                                            <MdDeleteForever size={16} />
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5 opacity-50">
                                        <CiWarning size={60} className="text-muted mb-3" />
                                        <h4>No hay documentos subidos</h4>
                                        <p className="text-muted">Sube los documentos necesarios para completar la oferta.</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {oferta && (
                    <SubirArchivoOferta
                        show={showSubirModal}
                        close={setShowSubirModal}
                        oferta={oferta} // Requires {id, name, sector_type} which matches our state structure
                        onUploadSuccess={fetchArchivos}
                    />
                )}

                <SendSignaturitModal
                    show={showSignaturitModal}
                    onHide={() => setShowSignaturitModal(false)}
                    onClose={fetchArchivos}
                    defaultEmail={getOfferSignaturitEmail(oferta)}
                    onConfirm={handleSendSignaturit}
                />
            </Container>
        </div>
    );
};

const OfertaDocumentacion = (props) => {
    const { sector_type, oferta_id } = useParams();
    return (
        <Layout
            offerContext={{ sector_type, oferta_id }}
        >
            <OfferWizardWrapper currentStep="documentacion">
                <OfertaDocumentacionContent {...props} />
            </OfferWizardWrapper>
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    user: state.Auth.user,
});

export default connect(mapStateToProps)(OfertaDocumentacion);
